import { useBreakpoint } from "@povio/ui";
import { cx } from "class-variance-authority";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Placement } from "react-aria";
import { ComboBox, type ComboBoxProps, Input, type Key, ListBox, ListBoxItem, Popover } from "react-aria-components";
import useMeasure from "react-use-measure";
import { useDebounceValue } from "usehooks-ts";

import { ArrowDropDownIcon } from "@/assets/icons/general/ArrowDropDown";
import { InlineIconButton } from "@/components/ui/buttons/InlineIconButton/InlineIconButton";
import {
  AutocompleteListItem,
  autocompleteListItemStyles,
} from "@/components/ui/inputs/Autocomplete/AutocompleteListItem";
import { InputHeader } from "@/components/ui/inputs/shared/InputHeader";
import { type TextInputVariantProps, textInput } from "@/components/ui/inputs/TextInput/TextInputWrapper";
import { Loader } from "@/components/ui/status/Loader/Loader";
import { Typography } from "@/components/ui/text/Typography/Typography";

const NEW_ITEM_ID = "new-item-id";
const NEW_ITEM_MIN_LENGTH = 3;
const THRESHOLD_FOR_FETCHING_NEXT_PAGE = 150;

export type AutocompleteItem<TKey extends Key = Key> = {
  id: TKey;
  label: string;
};

type AutocompleteProps<T extends object, TKey extends Key = Key> = Omit<ComboBoxProps<T>, "children"> &
  TextInputVariantProps & {
    label: string;
    items: AutocompleteItem<TKey>[];
    onCreateNewOption?: (value: string) => void;
    resetInputAfterSelection?: boolean;
    newItemTypeLabel?: string;
    placeholder?: string;
    hasNextPage?: boolean;
    fetchNextPage?: () => void;
    isFetchingNextPage?: boolean;
    onSearchChange?: (value: string) => void;
    isLoading?: boolean;
    showNewItemOption?: boolean;
    unselectOption?: boolean;
    inputClassname?: string;
    inputIconClassname?: string;
    inputIconPositionClassname?: string;
    hideLabels?: boolean;
    comboboxClassName?: string;
    inputIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
    inputIconPosition?: "left" | "right";
    "data-testid"?: string;
    placement?: Placement;
    shouldFlip?: boolean;
    error?: string;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    renderCustomRow?: (item: AutocompleteItem<TKey>) => React.ReactNode;
    emptyState?: React.ReactNode;
  };

export const Autocomplete = <T extends object, TKey extends Key = Key>({
  variant,
  label,
  items,
  onCreateNewOption,
  onSelectionChange,
  onInputChange,
  resetInputAfterSelection = false,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  onSearchChange,
  isLoading,
  showNewItemOption,
  newItemTypeLabel,
  placeholder,
  isRequired,
  unselectOption,
  inputClassname,
  inputIconClassname,
  inputIconPositionClassname,
  hideLabels,
  comboboxClassName,
  inputIcon: InputIcon,
  inputIconPosition = "right",
  "data-testid": dataTestId,
  placement = "bottom start",
  shouldFlip = true,
  error,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
  renderCustomRow,
  emptyState,
  ...props
}: AutocompleteProps<T, TKey>) => {
  const [triggerRef, { width }] = useMeasure();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(
    items.find((item) => item.id === props.selectedKey)?.label || props.inputValue || "",
  );
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [hasTriggeredFetch, setHasTriggeredFetch] = useState(false);
  const [debouncedValue] = useDebounceValue(value, 500);
  const justOpenedRef = useRef(false);

  const isDesktop = useBreakpoint("xl");

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      justOpenedRef.current = true;
      // Clear the flag after a short delay
      setTimeout(() => {
        justOpenedRef.current = false;
      }, 150);
    }

    if (controlledOnOpenChange) {
      controlledOnOpenChange(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  const [selectedKey, setSelectedKey] = useState<Key | null>(props.selectedKey || null);

  // Sync selectedKey with parent props
  useEffect(() => {
    if (props.selectedKey !== undefined) {
      setSelectedKey(props.selectedKey);
    }
  }, [props.selectedKey]);

  const showNewItem =
    showNewItemOption &&
    value.length >= NEW_ITEM_MIN_LENGTH &&
    !items?.find((item) => item?.label?.toLowerCase() === value.trim().toLowerCase());

  const newItems: (AutocompleteItem<TKey> & { isNewItem?: boolean })[] = [
    ...(showNewItem ? [{ id: NEW_ITEM_ID as TKey, label: value, isNewItem: true }] : []),
    ...items,
  ];

  useEffect(() => {
    setHasTriggeredFetch(false);
  }, [items.length, isOpen]);

  useEffect(() => {
    onSearchChange?.(debouncedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const inputChangeHandler = (val: string) => {
    setValue(val);
    onInputChange?.(val);
  };

  const selectionChangeHandler = (id: Key | null) => {
    // Ignore selections that happen right after opening (auto-focus events)
    if (justOpenedRef.current) {
      return;
    }

    // Don't process if no selection or if it's the same as current (and no unselect option)
    if (id === null) {
      return;
    }

    // Handle unselect case
    if (id === selectedKey && unselectOption) {
      setSelectedKey(null);
      setValue("");
      onSelectionChange?.(null);
      handleOpenChange(false);
      return;
    }

    // Ignore if selecting the same item (no action needed)
    if (id === selectedKey && !unselectOption) {
      return;
    }

    // Handle new item creation
    if (id === NEW_ITEM_ID) {
      onCreateNewOption?.(value);
      handleOpenChange(false);
      return;
    }

    // Handle normal selection
    onSelectionChange?.(id);
    setValue(items.find((item) => item.id === id)?.label || "");

    if (resetInputAfterSelection) {
      setValue("");
      setSelectedKey(id);
      setTimeout(() => {
        setSelectedKey(null);
      }, 100);
    } else {
      setSelectedKey(id);
    }

    // Close only after successful selection
    handleOpenChange(false);
  };

  const onScroll = useCallback(() => {
    if (!hasNextPage || !fetchNextPage || !scrollContainerRef.current || isFetchingNextPage || hasTriggeredFetch)
      return;

    const { scrollTop, clientHeight, scrollHeight } = scrollContainerRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - THRESHOLD_FOR_FETCHING_NEXT_PAGE;

    if (isNearBottom) {
      setHasTriggeredFetch(true);
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, hasTriggeredFetch]);

  return (
    <ComboBox
      {...props}
      onInputChange={inputChangeHandler}
      onSelectionChange={selectionChangeHandler}
      selectedKey={selectedKey || props.selectedKey}
      inputValue={value}
      allowsEmptyCollection
      onOpenChange={handleOpenChange}
      className={comboboxClassName}
    >
      <InputHeader
        label={label}
        required={isRequired}
        hideLabels={hideLabels}
      />
      <div className="relative">
        {inputIconPosition === "left" && (
          <div
            className={cx(
              "absolute top-1/2 left-input-side-spacing z-10 flex -translate-y-1/2 items-center",
              inputIconPositionClassname,
            )}
          >
            <InlineIconButton
              label=""
              icon={InputIcon || ArrowDropDownIcon}
              className={cx("text-text-default-2", inputIconClassname)}
            />
          </div>
        )}
        <Input
          className={textInput({
            variant,
            className: cx(inputIconPosition === "left" ? "pl-10" : "pr-8", inputClassname),
          })}
          ref={triggerRef}
          value={value}
          placeholder={placeholder}
          data-testid={dataTestId}
          autoComplete="off"
        />
        {inputIconPosition === "right" && (
          <div className="absolute top-1/2 right-input-side-spacing flex -translate-y-1/2 items-center">
            <InlineIconButton
              label=""
              icon={InputIcon || ArrowDropDownIcon}
              className={cx("text-secondary-75", inputIconClassname)}
            />
          </div>
        )}
      </div>

      <Popover
        className="my-4 dd:w-auto dm:w-[calc(100%-32px)] overflow-hidden outline-hidden"
        style={{ width: isDesktop ? width : "calc(100% - 32px)" }}
        offset={-10}
        isOpen={isOpen}
        placement={placement}
        shouldFlip={shouldFlip}
      >
        <div
          ref={scrollContainerRef}
          className="h-auto max-h-[300px] overflow-hidden overflow-y-auto rounded-input-rounding border border-elevation-outline-default-1 bg-elevation-fill-default-1 shadow-5 outline-hidden"
          onScroll={onScroll}
        >
          <ListBox className="outline-hidden">
            {isLoading && (
              <ListBoxItem
                id={undefined}
                isDisabled
                className={cx(
                  autocompleteListItemStyles.item({ isNewItem: false }),
                  "flex flex-row items-center gap-2",
                )}
              >
                <Loader className="h-4 w-4" />
                <span>Loading...</span>
              </ListBoxItem>
            )}
            {!isLoading && newItems.length === 0 && emptyState && (
              <ListBoxItem
                id={undefined}
                isDisabled
                className={cx(autocompleteListItemStyles.item({ isNewItem: false }))}
              >
                {emptyState}
              </ListBoxItem>
            )}
            {newItems.map((item, index) => (
              <AutocompleteListItem
                key={item.id}
                index={index}
                item={item}
                newItemTypeLabel={newItemTypeLabel}
                isNewItem={item.isNewItem}
                renderCustomRow={renderCustomRow}
              />
            ))}
            {isFetchingNextPage && (
              <ListBoxItem
                id={undefined}
                isDisabled
                className={cx(
                  autocompleteListItemStyles.item({ isNewItem: false }),
                  "flex flex-row items-center gap-2",
                )}
              >
                <Loader className="h-4 w-4" />
                <span>Loading...</span>
              </ListBoxItem>
            )}
          </ListBox>
        </div>
      </Popover>

      {error && (
        <Typography
          className={cx("mt-1 font-weight-600 text-danger")}
          size="labels-xs"
          variant="prominent-2"
        >
          {error}
        </Typography>
      )}
    </ComboBox>
  );
};
